import { LayoutSegment, VideoLayoutTimeline } from '@sparemin/api-sdk';
import { Map } from 'immutable';
import { last } from 'underscore';
import { EmbedLayoutSegment, EmbedVideoState } from 'redux/modules/embed/types';
import { MediaSegment, VideoLayoutTimelineById } from 'redux/modules/entities';
import { IEmbedConfig, ScenePreset } from 'types';
import { addGapsToTime } from './media-segment';
import { FormatStateForEmbedConfigOptions } from './types';

export type VideoLayoutTimelineIdData = Pick<
  VideoLayoutTimeline,
  'uuid' | 'revisionId'
>;

type GetScenePresetDisplayNameOptions = {
  truncate?: boolean;
};

export function getScenePresetDisplayName(
  preset: ScenePreset,
  opts?: GetScenePresetDisplayNameOptions,
) {
  const { truncate = false } = opts ?? {};

  if (preset === ScenePreset.CURRENT) {
    return 'Current';
  }

  if (preset === ScenePreset.FIT) {
    return 'Fit';
  }

  if (preset === ScenePreset.FULL_SCREEN) {
    return truncate ? 'Full' : 'Full Screen';
  }

  if (preset === ScenePreset.SPLIT) {
    return 'Split';
  }

  if (preset === ScenePreset.THREE) {
    return 'Three';
  }

  if (preset === ScenePreset.FOUR) {
    return 'Four';
  }

  return 'Split';
}

/**
 * Uses the segment's configuration from the API to generate a display name
 */
export function getLayoutSegmentDisplayName(
  segment: LayoutSegment,
  opts?: GetScenePresetDisplayNameOptions,
) {
  const slotCount = segment.slots.length;
  const slotTop = parseFloat(segment.slots[0].position?.top);
  const slotLeft = parseFloat(segment.slots[0].position?.left);

  if (slotCount === 1) {
    if (slotTop === 0 && slotLeft === 0) {
      return getScenePresetDisplayName(ScenePreset.FULL_SCREEN, opts);
    }
    return getScenePresetDisplayName(ScenePreset.FIT, opts);
  }

  return getScenePresetDisplayName(slotCount, opts);
}

export function getLayoutTimelineOriginMillis(timeline: LayoutSegment[]) {
  return timeline?.[0]?.playOffsetMillis ?? 0;
}

/**
 * calculates the total duration of the timeline.
 *
 * The layout timeline should be contiguous with no gaps.
 */
export function getLayoutTimelineDurationMillis(timeline: LayoutSegment[]) {
  const layoutStartMillis = getLayoutTimelineOriginMillis(timeline);

  const lastSegment = last(timeline);
  const layoutEndMillis =
    lastSegment?.playOffsetMillis + lastSegment?.durationMillis ?? 0;

  return layoutEndMillis - layoutStartMillis;
}

export function getVideoLayoutTimelineIds(
  config: IEmbedConfig | undefined,
): VideoLayoutTimelineIdData[] {
  const { videoClips = [] } = config ?? {};

  return videoClips.reduce((acc, { layoutTimelineInfo }) => {
    if (layoutTimelineInfo) {
      const { layoutTimelineId, revisionId } = layoutTimelineInfo;

      if (layoutTimelineId && revisionId) {
        acc.push({
          uuid: layoutTimelineId,
          revisionId,
        });
      }
    }
    return acc;
  }, []);
}

// named for consistency with other functions, this actually takes the layout
// data from entities and formats it for the editor state
export function formatVideoLayoutInfoFromConfig(
  videos: Map<string, EmbedVideoState>,
  opts?: FormatStateForEmbedConfigOptions,
) {
  const { videoLayoutTimelineById } = opts ?? {};

  if (!videoLayoutTimelineById) {
    return undefined;
  }

  return videos.reduce((videoLayoutInfoById, video) => {
    if (video.layoutInfoId) {
      const videoLayoutInfo = videoLayoutTimelineById.get(video.layoutInfoId);

      const state = Map({
        id: video.layoutInfoId,
        lastSavedRevisionId: 0,
        revisionId: 0,
        segments: videoLayoutInfo.get('data').toList(),
      }) as any;

      return videoLayoutInfoById.set(video.layoutInfoId, state);
    }
    return videoLayoutInfoById;
  }, Map() as VideoLayoutTimelineById);
}

export function formatVideoLayoutForConfig(
  uuid: string,
  revisionId: string,
  layoutSegments: EmbedLayoutSegment[],
  mediaSegments: MediaSegment[],
) {
  // const segments: EmbedLayoutSegment[] = layoutInfoState.get('segments').toJS();

  layoutSegments.sort((a, b) => a.playOffsetMillis - b.playOffsetMillis);

  return {
    uuid,
    revisionId,
    data: layoutSegments.map(segment => {
      const rawStartMillis = segment.playOffsetMillis;
      const rawEndMillis = rawStartMillis + segment.durationMillis;

      const startMillis = addGapsToTime(rawStartMillis, mediaSegments);
      const endMillis = addGapsToTime(rawEndMillis, mediaSegments);

      return {
        playOffsetMillis: startMillis,
        durationMillis: endMillis - startMillis,
        segmentId: segment.segmentId,
        slots: segment.slots,
        subjects: segment.subjects,
      };
    }),
  };
}
